import React, { useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from '../services/supabase';
import { FUNCTIONS_URL } from '../services/supabaseConfig';

interface BoostDef {
  id: string;
  name: string;
  description: string;
  boost_type: string;
  target_attribute: string;
  boost_value: number;
  rarity: string;
  icon: string;
  forge_rank: number;
  player_context: string;
  shop_price: number;
}

interface BoostShopPageProps {
  userData?: any;
  onNavigate?: (page: string) => void;
}

const RARITY_COLORS: Record<string, string> = {
  common: '#9ca3af',
  uncommon: '#22c55e',
  rare: '#3b82f6',
  epic: '#a855f7',
  legendary: '#eab308',
};

const RARITY_LABELS: Record<string, string> = {
  common: 'Common',
  uncommon: 'Uncommon',
  rare: 'Rare',
  epic: 'Epic',
  legendary: 'Legendary',
};

const RANK_LABELS: Record<number, string> = {
  1: 'Rank 1',
  2: 'Rank 2',
  3: 'Rank 3',
  4: 'Rank 4',
  5: 'Rank 5',
};

const ATTR_ICONS: Record<string, string> = {
  power: '💪', contact: '🎯', speed: '⚡', fielding: '🧤', arm: '🦾',
  discipline: '🧠', endurance: '🫀', velocity: '🔥', control: '🎯',
  movement: '🌀', deception: '🎭', stamina: '🔋',
};

const ATTR_DISPLAY: Record<string, string> = {
  power: 'Power', contact: 'Contact', speed: 'Speed', fielding: 'Fielding', arm: 'Arm',
  discipline: 'Discipline', endurance: 'Endurance', velocity: 'Velocity', control: 'Control',
  movement: 'Movement', deception: 'Deception', stamina: 'Stamina',
};

const CONTEXT_LABELS: Record<string, string> = {
  hitter: '⚾ Hitter',
  pitcher: '🎯 Pitcher',
  any: '🌐 Any',
};

type FilterType = 'all' | 'hitter' | 'pitcher' | 'any';
type RankFilter = 0 | 1 | 2 | 3 | 4 | 5; // 0 = all

const BoostShopPage: React.FC<BoostShopPageProps> = ({ userData, onNavigate }) => {
  const [catalog, setCatalog] = useState<BoostDef[]>([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState<string | null>(null);
  const [contextFilter, setContextFilter] = useState<FilterType>('all');
  const [rankFilter, setRankFilter] = useState<RankFilter>(0);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [balance, setBalance] = useState(userData?.balls_balance || 0);

  useEffect(() => {
    loadCatalog();
  }, []);

  useEffect(() => {
    setBalance(userData?.balls_balance || 0);
  }, [userData?.balls_balance]);

  async function loadCatalog() {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${FUNCTIONS_URL}/boost`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session ? { 'Authorization': `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({ action: 'shop-catalog' }),
      });

      const data = await res.json();
      if (data.catalog) {
        setCatalog(data.catalog);
      }
    } catch (e) {
      console.error('Load catalog error:', e);
    }
    setLoading(false);
  }

  async function handleBuy(boostDef: BoostDef) {
    if (!isSupabaseConfigured() || !supabase) return;
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) { setMessage({ text: 'Sign in to purchase', type: 'error' }); return; }

    setBuying(boostDef.id);
    setMessage(null);

    try {
      const res = await fetch(`${FUNCTIONS_URL}/boost`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ action: 'buy-boost', boostDefId: boostDef.id }),
      });

      const data = await res.json();
      if (data.success) {
        setBalance(data.newBalance);
        setMessage({ text: `${boostDef.name} purchased!`, type: 'success' });
      } else {
        setMessage({ text: data.error || 'Purchase failed', type: 'error' });
      }
    } catch (e) {
      setMessage({ text: 'Network error', type: 'error' });
    }
    setBuying(null);
  }

  // Filter catalog
  const filtered = catalog.filter(b => {
    if (contextFilter !== 'all' && b.player_context !== contextFilter) return false;
    if (rankFilter !== 0 && b.forge_rank !== rankFilter) return false;
    return true;
  });

  // Group by rank
  const grouped = filtered.reduce<Record<number, BoostDef[]>>((acc, b) => {
    if (!acc[b.forge_rank]) acc[b.forge_rank] = [];
    acc[b.forge_rank].push(b);
    return acc;
  }, {});

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
        Loading shop...
      </div>
    );
  }

  return (
    <div style={{ padding: '0 0 40px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 800 }}>
            <span className="gradient-text">⚡ Boost Shop</span>
          </h1>
          <p style={{ margin: '4px 0 0', color: '#9ca3af', fontSize: '0.9rem' }}>
            Power up your roster
          </p>
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '6px 14px', background: 'rgba(200,150,26,0.08)',
          borderRadius: 100, border: '1px solid rgba(200,150,26,0.15)',
        }}>
          <span style={{ fontSize: '0.72rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Balance</span>
          <span style={{ fontSize: '0.95rem', fontWeight: 800, color: '#e8b82a', fontVariantNumeric: 'tabular-nums' }}>
            {balance.toLocaleString()} $BALLS
          </span>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div style={{
          padding: '10px 16px', borderRadius: 8, marginBottom: 16,
          background: message.type === 'success' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
          border: `1px solid ${message.type === 'success' ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
          color: message.type === 'success' ? '#22c55e' : '#ef4444',
          fontSize: '0.85rem',
        }}>
          {message.text}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {/* Context filter */}
        {([['all', 'All'], ['hitter', '⚾ Hitter'], ['pitcher', '🎯 Pitcher'], ['any', '🌐 Any']] as [FilterType, string][]).map(([val, label]) => (
          <button key={val} onClick={() => setContextFilter(val)} style={{
            padding: '6px 14px', borderRadius: 6, border: '1px solid',
            borderColor: contextFilter === val ? '#d4a017' : '#374151',
            background: contextFilter === val ? 'rgba(212,160,23,0.1)' : 'transparent',
            color: contextFilter === val ? '#e8b82a' : '#9ca3af',
            fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
          }}>
            {label}
          </button>
        ))}

        <span style={{ width: 1, background: '#374151', margin: '0 4px' }} />

        {/* Rank filter */}
        {([0, 1, 2, 3, 4, 5] as RankFilter[]).map(rank => (
          <button key={rank} onClick={() => setRankFilter(rank)} style={{
            padding: '6px 12px', borderRadius: 6, border: '1px solid',
            borderColor: rankFilter === rank ? '#d4a017' : '#374151',
            background: rankFilter === rank ? 'rgba(212,160,23,0.1)' : 'transparent',
            color: rankFilter === rank ? '#e8b82a' : '#9ca3af',
            fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
          }}>
            {rank === 0 ? 'All Ranks' : `+${[0,1,3,5,8,10][rank]}`}
          </button>
        ))}
      </div>

      {/* Catalog grouped by rank */}
      {Object.entries(grouped).sort(([a], [b]) => Number(a) - Number(b)).map(([rank, boosts]) => (
        <div key={rank} style={{ marginBottom: 28 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #1e293b',
          }}>
            <span style={{ color: RARITY_COLORS[boosts[0]?.rarity || 'common'], fontWeight: 700, fontSize: '0.95rem' }}>
              {RANK_LABELS[Number(rank)] || `Rank ${rank}`}
            </span>
            <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>
              +{boosts[0]?.boost_value} to attribute
            </span>
            <span style={{ color: '#e8b82a', fontSize: '0.8rem', fontWeight: 600, marginLeft: 'auto' }}>
              {boosts[0]?.shop_price?.toLocaleString()} $BALLS each
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 8 }}>
            {boosts.sort((a, b) => a.target_attribute.localeCompare(b.target_attribute)).map(b => {
              const color = RARITY_COLORS[b.rarity] || '#9ca3af';
              const canAfford = balance >= (b.shop_price || 0);
              const isBuying = buying === b.id;
              return (
                <div key={b.id} style={{
                  background: 'rgba(16,24,40,0.6)',
                  border: `1px solid ${color}22`,
                  borderRadius: 10,
                  padding: 12,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                  transition: 'border-color 0.15s',
                }}
                  onMouseEnter={e => (e.currentTarget.style.borderColor = `${color}55`)}
                  onMouseLeave={e => (e.currentTarget.style.borderColor = `${color}22`)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 18 }}>{ATTR_ICONS[b.target_attribute] || '⬆️'}</span>
                    <span style={{ color, fontSize: '0.82rem', fontWeight: 700 }}>{b.name}</span>
                  </div>
                  <div style={{ color: '#6b7280', fontSize: '0.7rem' }}>
                    {CONTEXT_LABELS[b.player_context] || b.player_context}
                  </div>
                  <button
                    onClick={() => handleBuy(b)}
                    disabled={!canAfford || isBuying}
                    style={{
                      marginTop: 4,
                      padding: '6px 0',
                      borderRadius: 6,
                      border: 'none',
                      background: canAfford
                        ? `linear-gradient(135deg, ${color}cc, ${color}88)`
                        : '#1e293b',
                      color: canAfford ? '#fff' : '#4b5563',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      cursor: canAfford ? 'pointer' : 'not-allowed',
                      transition: 'all 0.15s',
                    }}
                  >
                    {isBuying ? '...' : canAfford ? `${b.shop_price?.toLocaleString()} $BALLS` : 'Not enough'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {filtered.length === 0 && (
        <div style={{ textAlign: 'center', color: '#6b7280', padding: 40 }}>
          No boosts match your filters
        </div>
      )}
    </div>
  );
};

export default BoostShopPage;
